import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

// Edit / cancel allowed only before QC is checking it and before any movement
export const changeable = (p) => p.movement_status === "unassigned" && !["closed", "cancelled"].includes(p.status) && !["ready_for_qc", "on_hold"].includes(p.qc_status);
export const needsApproval = (p) => p.planning_status === "planned" || p.change_hold;

export function ApprovalNote({ p, action }) {
  if (!needsApproval(p) && p.qc_status !== "approved") return null;
  return (
    <div className="flex gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="plan-reapproval-note">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        {needsApproval(p) && <>This {action} will <b>send {p.planned_dispatch_number} for re-approval</b>. Until an LM approver decides, it cannot go to QC or onto a movement.{" "}</>}
        {action === "edit" && p.qc_status === "approved" && <>QC already approved this plan - after the edit <b>QC must check it again</b>.</>}
        {p.change_hold && <> A change is already waiting; this replaces it.</>}
      </span>
    </div>
  );
}

export function CancelPlanDialog({ p, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/planned-dispatches/${p.id}/cancel`, { reason });
      toast.success(r.data.change?.approval_retriggered ? `Cancellation of ${p.planned_dispatch_number} sent for approval` : `${p.planned_dispatch_number} cancelled`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md w-[95vw]" data-testid="plan-cancel-dialog">
        <DialogHeader><DialogTitle>Cancel {p.planned_dispatch_number}?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">The plan is kept with its full history and marked Cancelled - nothing is deleted. Its quantity becomes free to plan again{needsApproval(p) ? " once the cancellation is approved" : ""}.</p>
        <ApprovalNote p={p} action="cancellation" />
        <div><Label>Reason for cancelling *</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="plan-cancel-reason" /></div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="plan-cancel-back">Keep plan</Button>
          <Button variant="destructive" disabled={busy || reason.trim().length < 3} onClick={go} data-testid="plan-cancel-confirm">{needsApproval(p) ? "Request cancellation" : "Cancel plan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Diff({ c }) {
  const b = c.before_summary || {}, a = c.after_summary;
  if (!a) return <span className="text-red-700">Cancel plan</span>;
  const keys = [["expected_dispatch_date", "Date"], ["customer_required_by_date", "Required by"], ["address", "Address"], ["remarks", "Remarks"], ["planning_status", "Status"], ["invoices", "Invoices"]];
  const items = [...new Set([...Object.keys(b.items || {}), ...Object.keys(a.items || {})])].filter((k) => (b.items || {})[k] !== (a.items || {})[k]);
  const rows = [...keys.filter(([k]) => String(b[k] ?? "") !== String(a[k] ?? "")).map(([k, l]) => `${l}: ${b[k] || "—"} → ${a[k] || "—"}`),
    ...items.map((k) => `${k}: ${(b.items || {})[k] ?? 0} → ${(a.items || {})[k] ?? 0}`)];
  return <span className="whitespace-normal">{rows.length ? rows.join(" · ") : "No field changed"}</span>;
}

export function ChangeDecision({ c, onDone }) {
  const { can, me } = useMe();
  const [mode, setMode] = useState(null);
  const [comment, setComment] = useState("");
  if (c.approval_status !== "pending" || !can("orders.approve")) return null;
  if (c.changed_by?.user_id === me.user_id && !(me.roles || []).includes("admin")) return <span className="text-[11px] text-slate-500" data-testid={`plan-change-own-${c.change_no}`}>Awaiting another approver</span>;
  const go = async () => {
    try {
      await api.post(`/approvals/plan-changes/${c.id}/decision`, { decision: mode, comment });
      toast.success(`${c.planned_dispatch_number}: change ${mode}`);
      setMode(null);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!mode) return (
    <span className="flex gap-1">
      <Button size="sm" className="h-7" onClick={() => setMode("approved")} data-testid={`plan-change-approve-${c.change_no}`}>Approve</Button>
      <Button size="sm" variant="outline" className="h-7" onClick={() => setMode("rejected")} data-testid={`plan-change-reject-${c.change_no}`}>Reject</Button>
    </span>
  );
  return (
    <span className="flex flex-col gap-1 min-w-[200px]">
      <Textarea rows={2} placeholder={mode === "rejected" ? "Why rejected (required)" : "Comment (optional)"} value={comment} onChange={(e) => setComment(e.target.value)} data-testid={`plan-change-comment-${c.change_no}`} />
      <span className="flex gap-1"><Button size="sm" className="h-7" onClick={go} data-testid={`plan-change-confirm-${c.change_no}`}>Confirm {mode}</Button><Button size="sm" variant="ghost" className="h-7" onClick={() => setMode(null)}>Back</Button></span>
    </span>
  );
}

export function ChangeRows({ rows, onDone, showPlan }) {
  return (
    <ul className="space-y-2" data-testid="plan-change-list">
      {rows.map((c) => (
        <li key={c.id} className="rounded border border-slate-200 bg-white p-2 text-xs" data-testid={`plan-change-${c.change_no}`}>
          <div className="flex flex-wrap items-center gap-2">
            <b className="font-mono">{c.change_no}</b>{showPlan && <Link to={`/dispatch-planning?focus=${c.planned_dispatch_id}`} className="font-mono text-sky-700 hover:underline" data-testid={`plan-change-open-${c.change_no}`}>{c.planned_dispatch_number} · {c.lm_number} →</Link>}
            <span className="uppercase text-[10px] font-semibold text-slate-500">{c.kind}</span>
            <StatusBadge status={c.approval_status} testid={`plan-change-status-${c.change_no}`} />
            {c.approval_retriggered && <span className="text-[10px] text-amber-700">re-approval triggered</span>}
            {c.qc_superseded && <span className="text-[10px] text-amber-700">QC re-check needed</span>}
            <span className="ml-auto"><ChangeDecision c={c} onDone={onDone} /></span>
          </div>
          <div className="mt-1 text-slate-700"><Diff c={c} /></div>
          <div className="mt-1 text-slate-500">Reason: {c.change_reason || "—"} · by {c.changed_by?.name} · {fmtDateTime(c.changed_at)}
            {c.decided_by && ` · ${c.approval_status} by ${c.decided_by.name} ${fmtDateTime(c.decided_at)}${c.decision_comment ? ` - “${c.decision_comment}”` : ""}`}</div>
        </li>
      ))}
    </ul>
  );
}

export function PlanChangeHistory({ planId, onDone }) {
  const { data, reload } = useFetch(`/approvals/plan-changes?status=all&plan_id=${planId}`);
  if (!data?.length) return null;
  return <div data-testid="plan-change-history"><div className="mb-1 text-xs font-semibold text-slate-700">Change history</div><ChangeRows rows={data} onDone={() => { reload(); onDone?.(); }} /></div>;
}

export function PendingPlanChanges({ lmId = "", compact = false, onDone }) {
  const [status, setStatus] = useState("pending");
  const { data, reload } = useFetch(`/approvals/plan-changes?status=${status}${lmId ? `&lm_id=${lmId}` : ""}`);
  if (compact && !data?.length) return null;
  return (
    <section className={compact ? "mb-4" : "mb-8"} data-testid="pending-plan-changes">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-base font-semibold">Dispatch plan changes{status === "pending" && data?.length ? <span className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 text-xs text-white" data-testid="plan-changes-count">{data.length} waiting</span> : null}</h2>
        {["pending", "all"].map((s) => <Button key={s} size="sm" variant={status === s ? "default" : "outline"} className="h-7" onClick={() => setStatus(s)} data-testid={`plan-changes-filter-${s}`}>{s === "pending" ? "Pending" : "All"}</Button>)}
      </div>
      {data?.length ? <ChangeRows rows={data} onDone={() => { reload(); onDone?.(); }} showPlan /> : <p className="text-xs text-slate-500" data-testid="plan-changes-empty">No dispatch plan changes {status === "pending" ? "waiting" : "yet"}</p>}
    </section>
  );
}
